import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { Skill } from '../types';

export const createPromoAdSkill: Skill = {
    name: SKILL_NAMES.createPromoAd,
    description:
        'A fast, punchy promotional video with short fragment-paced scenes, a confident energetic voiceover, and high-energy music driving to a single call to action. Use for product ads, promotions, offers, and launch spots.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        title: 'Promo Ad',
        preview: 'promo-ad',
        frames: ['promo-ad-1', 'promo-ad-2', 'promo-ad-3', 'promo-ad-4', 'promo-ad-5'],
    },
    instructions: `# Create Promo Ad
For a fast-paced, high-energy promotional video that drives to a clear call to
action: short scenes (2-3 seconds each), copy written in punchy sentence
fragments rather than full sentences, a confident/energetic voice, and
fast/positive/high-energy music.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- The main selling point — the single core message or offer the ad is built
  around. Everything in the script should serve this one point, not a
  scattershot of several
- The call to action to close on
- Ideal duration — ad-style videos skew short; don't default to a long preset
- Any specific media that needs to appear (product shots, logos, footage)
- Voice (search according to below instructions to offer good options)

Show users options of templates with an energetic, promotional feel. Search if none
of the available ones seem an obvious fit.

## Narration and music
- **Voice: confident and energetic, chosen by the user from a shortlist.**
  Search with \`${TOOL_NAMES.searchVoices}\` for a tone that reads as upbeat, assured, and
  driven — pass "confident"/"energetic"/"professional" through the \`search\`
  free-text field alongside whatever \`tone\` values the tool recognizes.
  Recommend a handful of the best-fitting high-energy professional results
  to the user, and use whichever one they pick. Don't default to the first search result unasked — this format's
  vocal character is high-energy, but the specific voice is a real choice
  the user gets to make.
- **Music: fast-paced, positive, high-energy — and it must actually end up
  in the video.** Search \`${TOOL_NAMES.searchMusic}\` for upbeat, driving tracks with real
  momentum, then apply the chosen track to the video (don't stop at
  searching) and set its volume so it stays energetic without burying the
  voiceover. Before moving on, confirm with \`${TOOL_NAMES.getVideoState}\` that a music
  track is actually attached — a video with no music applied is a defect in
  this format specifically, since the energy of the ad depends on it.
- **Never use em dashes (—)** in the script or in on-screen text. Write the
  two halves as separate sentences, or join with a comma or conjunction.
- Keep pauses minimal and deliberate — at most a beat right before the call
  to action lands, so it hits with weight. This format's whole point is
  momentum; don't slow the read down elsewhere.

## Script — punchy fragments, one selling point
- Tone is **promotional, like an advertisement**: confident, direct, sells the
  main selling point from Setup. Not educational, not a neutral explainer.
- **Write in sentence fragments, not full sentences.** Where a normal script
  would write "Our new formula lasts twice as long and costs half as much,"
  this format breaks that into fragments delivered back to back: "Lasts twice
  as long." "Costs half as much." Each fragment is its own scene beat. This is
  a deliberate structural choice for this format, not a shortcut — write the
  fragments themselves, don't write full sentences and then chop them.
- **The governing rule for how much text belongs in a scene still applies:
  exactly the words spoken during that scene's on-screen time — no more, no
  less.** The difference in this format is what counts as "the words spoken"
  in the first place: because the script itself is written as short
  fragments, each fragment legitimately is only a few words, and the on-screen
  text is that whole fragment, verbatim — never a further-trimmed piece of it,
  and never the fragment plus extra unspoken commentary bolted on.
- **Only use a scene's secondary text asset when the fragment naturally splits
  into two parts spoken in the same beat** (e.g. a short claim plus a shorter
  punch, like "New formula." / "Half the price.") — don't populate it just to
  avoid leaving it blank.
- Build the narrative spine first — selling point, proof points, then the
  CTA — before exact wording, so one changed beat doesn't force a full
  rewrite.
- Every fragment should trace back to the one main selling point from Setup —
  cut anything that's a tangent, even if it's a true or interesting claim.
- Write final wording budgeted to the target duration from Setup — at roughly
  2-3 seconds per scene, that duration sets the number of fragments the ad can
  hold. Cut fragments to fit, never stretch scene durations to fit more
  fragments than the runtime allows.
- **Keep every fragment genuinely short — a handful of words, not a clause.**
  If a fragment would take much more than 2-3 seconds to say, it's not a
  fragment yet: break it again. "Lasts twice as long and costs half as much"
  is still one clause; split it into "Lasts twice as long." and "Costs half
  as much." even though both halves came from what started as one sentence.
  In this format, splitting further to keep scenes snappy is correct, not the
  "fragmenting further than the script already broke it" mistake called out
  below — that caution is about not slicing a fragment into an unreadable
  scrap, not about permission to leave fragments long.
- **Close on the exact call to action gathered in Setup**, delivered as its
  own scene, never folded into a preceding fragment.
- **Every scene carries on-screen text tied to a spoken fragment by default —
  a scene with no text at all should never just happen.** The one exception
  is a deliberate, sparing quick-cut media-only scene used purely for mood or
  energy (a flash of product/lifestyle footage with nothing spoken over it,
  cutting fast to the beat of the music). That's a real, intentional choice
  this format allows, not the default state, and not a way to pad out the
  scene count: reach for it a handful of times at most in a given ad, never
  back-to-back, and only when you're deliberately choosing a wordless beat —
  not because a fragment ran out or a scene got built without checking it had
  text in it. If a scene ended up with no text and it wasn't a deliberate
  choice like this, that's a defect: give it back its fragment or cut it.

## Scene design variety
- **Use a variety of scene designs across the video.** Rotate across
  categories — media, collage, text, quote, lists, data/chart, animated
  captions, icon — rather than leaning on two or three; fast, varied cuts
  reinforce the high-energy feel.
- Never the same design on two consecutive scenes.
- **Match the design to the content**: does each text asset comfortably fit a
  short fragment (shape-bounded assets — circles, badges, pills — hold one or
  two words, no more)? A design missing a needed asset is the wrong design.
- **A Quote design is for an actual attributed quotation** — a testimonial or
  someone's words, with a real name to credit them to. Promo copy, taglines
  and benefit fragments are not quotes, however punchy. The decorative
  quotation mark is usually a read-only asset you cannot remove, so blanking
  the name/role slots leaves it stranded over copy nobody said. No speaker to
  credit means a text design, not a quote design. Most quote designs supply
  the quotation mark themselves, so don't add quote characters unless the
  design's own placeholder text already has them.
- **Avoid back-to-back text-only scenes** — after a text scene, the next one
  should carry media; product/benefit fragments read stronger paired with an
  image or clip than with type alone.
- **Caption-style designs** (ANIMATED_CAPTIONS category) fit this format well
  given the fragment-driven pacing — a good recurring choice here, more so
  than in a slower-paced recipe, but still not every single scene.
- If you're deliberately using a quick-cut media-only mood scene (see Script
  above), pick a design with zero text assets on purpose, the same way
  Media Montage's media-only scenes work — don't leave a text-carrying
  design's text field blank by accident, use a design built to have none.

## Asset timing — short by design, not by shaving
- **Anchor each fragment's on-screen text to the exact words in the narration
  where that fragment is spoken** (\`${TOOL_NAMES.addVoiceoverScene}\` with \`words\` for that
  fragment) — the text must match the words its scene's \`words\` range
  covers exactly, not a trimmed-down piece of it and not padded past it.
- **This format's target scene length is genuinely short — about 2-3
  seconds — and that is correct here, not a defect to fix.** In the other
  recipes in this set, a 2-3 second span is a warning sign that a natural
  clause got sliced too thin. Here it isn't, because the script itself is
  written in short fragments rather than full clauses — a fragment like
  "Lasts twice as long" is genuinely only a few words long as written, and its
  scene should cover exactly those words, no more. The distinction is where
  the shortness comes from: it's fine when the fragment was written short on
  purpose; it's still a defect if a longer, natural fragment gets chopped
  mid-phrase just to hit a shorter duration, or if unspoken words get trimmed
  off what the scene displays.
- Split fragments from each other only at natural phrase boundaries — a
  fragment can and should be broken again into two shorter fragments/scenes
  when it runs long, but never split mid-word or mid-phrase in a way that
  breaks the grammar of what's left on each side.
- **Treat roughly 3 seconds as a hard ceiling on spoken duration per scene,
  not just a target.** After anchoring each fragment's \`words\` span, check
  how long that span actually takes to say (via \`${TOOL_NAMES.getVideoState}\` or
  \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`). Any scene running past about 3
  seconds is a defect in this format specifically — go back and split that
  fragment into two shorter ones (each becoming its own scene with its own
  design), rather than leaving one longer scene in an otherwise fast-cut ad.
  This applies even to a fragment that reads as "one idea" — one idea can
  still be two scenes if saying it takes too long.
- When one selling point needs more than one fragment to land, keep the design
  and media consistent across those consecutive scenes so it reads as one
  continuous beat rather than unrelated cuts.
- The pace should feel rapid-fire but each fragment still individually
  readable — if \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\` flags a scene as too short
  even for a short fragment, that fragment is genuinely too dense for 2-3
  seconds; shorten the fragment itself in the script rather than stretching
  the scene's duration. Too-short and too-long are both defects here, but
  given this format's whole point is momentum, err toward splitting a
  borderline scene rather than letting it run long.

## Media
- **The chosen clip or shot must obviously fit the selling point — a viewer
  half-paying-attention should get why it's on screen.** Vaguely-related is
  not good enough in this format: a fast cut with an awkward or mismatched
  shot reads far worse here than in a slower recipe, because there's no time
  for the viewer to puzzle out the connection before the next cut.
- **Use the user's own uploads for a scene only when they specifically named
  that media for that point in Setup.** Don't otherwise rummage through the
  user's general uploaded library for a stand-in shot just because something
  in it is loosely on-topic — an unrelated or awkwardly-fitting personal photo
  or clip, force-fit into a beat because it existed, is a defect here even
  though it's technically "their media."
- **For every other scene, load \`${SKILL_NAMES.findStockMedia}\` and pick from there** (or
  \`${SKILL_NAMES.findDamMedia}\` if a brand DAM is connected and the shot is brand-specific)
  — high-energy promotional stock that's a clean, obvious match beats a
  mediocre fit pulled from the user's uploads. Only widen the search to the
  user's broader library if stock and DAM both genuinely turn up nothing
  workable, and hold that fallback to the same obvious-fit bar — if nothing
  fits cleanly, prefer a text/data-style scene with no media over forcing in
  an awkward shot (a quote design only if the line really is an attributed
  quotation).
- Reframe within the asset so the subject is well-composed and nothing
  important sits under text (\`${SKILL_NAMES.cropMedia}\` skill).
- If the selling point includes a stat worth seeing (a discount, a comparison),
  make it a chart rather than just stating it.

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand (\`${SKILL_NAMES.applyBrandKit}\`
  skill); don't hand-pick color overrides unprompted.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Turn captions off** (\`${TOOL_NAMES.setCaptions}\` with \`enabled: false\`) unless the
  user specifically asked for them on.
- **Check every single scene's actual duration** (\`${TOOL_NAMES.getVideoState}\`), not just
  a sample — every one should land at roughly 2-3 seconds. Any scene running
  past about 3 seconds is a defect: split its fragment into two shorter ones
  and rebuild it as two scenes rather than leaving it long. The video overall
  should read as rapid-fire, with no scene that lingers.
- The script is genuinely written in fragments, not full sentences that
  happen to be short — check a handful of scenes' transcript spans against
  their on-screen text for an exact match in both directions.
- **Check every scene for on-screen text.** A scene with no text at all is
  only acceptable if it's a deliberate quick-cut mood scene used sparingly
  (not stacked back-to-back, not more than a handful across the ad) — if a
  scene turns up with no text and it wasn't meant as one of those, that's a
  defect: it's missing its fragment.
- Every fragment ties back to the single main selling point from Setup — no
  tangents, no scattershot claims.
- **Every piece of media obviously fits the beat it's on** — no awkward or
  vaguely-related shots. Any media pulled from the user's general uploads
  (rather than media they specifically named in Setup) should be reconsidered
  and, if it's not a clean fit, replaced with stock or dropped for a
  media-free scene design.
- **Spot-check that shortness comes from fragment-writing, not from cutting a
  longer natural clause mid-phrase.** If a scene reads like a chopped-off
  piece of a longer sentence rather than a complete punchy fragment, that's a
  defect specific to this format even though the duration itself looks right.
- Scene design rotates across categories with no back-to-back repeats, and
  text-only scenes aren't stacked next to each other.
- **Confirm a music track is actually applied** (\`${TOOL_NAMES.getVideoState}\`) — not
  just searched — and that it's genuinely fast-paced and high-energy, set at
  a volume that stays felt without burying the voiceover. A finished video
  with no music track is always a defect here.
- The voice used is the one the user picked from the shortlist in Setup, and
  it reads as confident and energetic.
- The video ends on the exact call to action gathered in Setup, as its own
  scene.
- Captions are off unless the user asked for them on.`,
};
